import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, MapPin, Loader2, Navigation } from 'lucide-react';

// Fix Leaflet/Webpack icon issues
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Google Places API Key
// Fallback to direct key if process.env is missing (e.g. restart pending)
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyB3FJ-Iy1zR_Xl2WRz11X-rUg--0HkW1Lo";

// Helper to update map view when position changes
const MapViewUpdater = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        map.flyTo(center, 17, { duration: 1.2 });
    }, [center, map]);
    return null;
};

const LocationPicker = ({ onLocationConfirm, initialLat, initialLon, initialAddress, isLight }) => {
    // Default to Athens if no location
    const defaultCenter = [37.9838, 23.7275];
    const [position, setPosition] = useState(initialLat && initialLon ? [initialLat, initialLon] : defaultCenter);
    const [addressQuery, setAddressQuery] = useState(initialAddress || '');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [sessionToken, setSessionToken] = useState(null);
    const debounceRef = useRef(null);

    // Generate a session token on mount (for Google billing optimization)
    useEffect(() => {
        setSessionToken(crypto.randomUUID());
    }, []);

    // Debounced search using Google Places Autocomplete
    const handleSearch = async (query) => {
        if (!query || query.length < 3) {
            setSuggestions([]);
            return;
        }

        // Clear previous debounce
        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                // Google Places Autocomplete API (New)
                const res = await fetch(
                    `https://places.googleapis.com/v1/places:autocomplete`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Goog-Api-Key': GOOGLE_API_KEY,
                        },
                        body: JSON.stringify({
                            input: query,
                            sessionToken: sessionToken,
                            includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'establishment'],
                            languageCode: 'en', // English results
                        })
                    }
                );
                const data = await res.json();

                if (data.suggestions) {
                    setSuggestions(data.suggestions.map(s => ({
                        placeId: s.placePrediction?.placeId,
                        displayName: s.placePrediction?.text?.text,
                        structuredFormat: s.placePrediction?.structuredFormat
                    })).filter(s => s.placeId));
                } else {
                    setSuggestions([]);
                }
            } catch (e) {
                console.error("Google Places Autocomplete error", e);
                setSuggestions([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);
    };

    // Helper to extract address components
    const parseComponents = (components, formattedAddress, fallbackName) => {
        let route = '';
        let streetNumber = '';
        let locality = '';
        let adminArea3 = '';

        if (components) {
            components.forEach(c => {
                if (c.types.includes('route')) route = c.longText || c.long_name;
                if (c.types.includes('street_number')) streetNumber = c.longText || c.long_name;
                if (c.types.includes('locality')) locality = c.longText || c.long_name;
                if (c.types.includes('administrative_area_level_3')) adminArea3 = c.longText || c.long_name;
            });
        }

        // 1. City logic: prefer locality (e.g. Pireas), fallback to admin_area_3 if locality is missing
        const city = locality || adminArea3 || '';

        // 2. Street Address logic (Street + Number)
        // If we have both, combine them. If only route, use it. If nothing, fallback to formatted.
        let address = '';
        if (route && streetNumber) {
            address = `${route} ${streetNumber}`;
        } else if (route) {
            address = route;
        } else {
            // If API didn't parse route/number well, try to start from formatted
            address = formattedAddress ? formattedAddress.split(',')[0] : fallbackName;
        }

        // 3. Full Address (Formatted string)
        const fullAddress = formattedAddress || fallbackName;

        return { city, address, fullAddress };
    };

    // Get place details (coordinates) when user selects a suggestion
    const handleSelectSuggestion = async (place) => {
        setIsSearching(true);
        setSuggestions([]);
        setAddressQuery(place.displayName);

        try {
            // Get Place Details to retrieve coordinates
            const res = await fetch(
                `https://places.googleapis.com/v1/places/${place.placeId}?fields=location,addressComponents,formattedAddress&languageCode=en`,
                {
                    method: 'GET',
                    headers: {
                        'X-Goog-Api-Key': GOOGLE_API_KEY,
                    }
                }
            );
            const data = await res.json();

            if (data.location) {
                const lat = data.location.latitude;
                const lon = data.location.longitude;
                setPosition([lat, lon]);

                const { city, address, fullAddress } = parseComponents(data.addressComponents, data.formattedAddress, place.displayName);

                onLocationConfirm({
                    lat,
                    lon,
                    city,
                    address: fullAddress,      // Main address is now the full string
                    street_address: address,   // "Serifou 31"
                    source: 'search'
                });

                // Generate new session token for next search
                setSessionToken(crypto.randomUUID());
            }
        } catch (e) {
            console.error("Google Place Details error", e);
        } finally {
            setIsSearching(false);
        }
    };

    const handleDragEnd = async (e) => {
        const marker = e.target;
        const newPos = marker.getLatLng();
        const { lat, lng } = newPos;
        setPosition([lat, lng]);

        // Reverse geocode using Google
        try {
            const res = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}&language=en`
            );
            const data = await res.json();

            if (data.results && data.results[0]) {
                const result = data.results[0];
                setAddressQuery(result.formatted_address);

                // Note: Geocoding API uses 'long_name' instead of 'longText'
                const { city, address, fullAddress } = parseComponents(result.address_components, result.formatted_address, result.formatted_address);

                onLocationConfirm({
                    lat,
                    lon: lng,
                    city,
                    address: fullAddress,
                    street_address: address,
                    source: 'pin'
                });
            } else {
                onLocationConfirm({
                    lat,
                    lon: lng,
                    city: '',
                    address: addressQuery,
                    street_address: '',
                    source: 'pin'
                });
            }
        } catch (e) {
            console.error("Reverse geocode error", e);
            onLocationConfirm({
                lat,
                lon: lng,
                address: addressQuery,
                street_address: '',
                source: 'pin'
            });
        }
    };

    return (
        <div className="space-y-4">
            {/* Autocomplete Input */}
            <div className="relative z-[1001]">
                <div className={`flex items-center border rounded-2xl px-4 py-3 transition-all ${isLight ? 'bg-white border-neutral-300 focus-within:border-neutral-900 focus-within:ring-1 focus-within:ring-neutral-900'
                    : 'bg-white/5 border-white/10 focus-within:border-white focus-within:ring-1 focus-within:ring-white'
                    }`}>
                    <Search size={18} className={isLight ? 'text-neutral-400' : 'text-neutral-500'} />
                    <input
                        type="text"
                        value={addressQuery}
                        onChange={(e) => {
                            setAddressQuery(e.target.value);
                            handleSearch(e.target.value);
                        }}
                        placeholder="Search for property address..."
                        className={`w-full bg-transparent border-none outline-none ml-3 font-medium ${isLight ? 'text-neutral-900 placeholder:text-neutral-400' : 'text-white placeholder:text-neutral-500'
                            }`}
                    />
                    {isSearching && <Loader2 size={16} className="animate-spin opacity-50" />}
                </div>

                {/* Suggestions Dropdown */}
                {suggestions.length > 0 && (
                    <div className={`absolute top-full left-0 right-0 mt-2 rounded-2xl border shadow-2xl overflow-hidden max-h-60 overflow-y-auto z-[1002] ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-800 border-neutral-700'
                        }`}>
                        {suggestions.map((place) => (
                            <button
                                key={place.placeId}
                                onClick={() => handleSelectSuggestion(place)}
                                className={`w-full text-left px-4 py-3 text-sm border-b last:border-0 transition-colors flex items-start gap-2 ${isLight ? 'border-neutral-100 hover:bg-neutral-50 text-neutral-800'
                                    : 'border-white/5 hover:bg-white/5 text-neutral-200'
                                    }`}
                            >
                                <MapPin size={14} className="mt-0.5 shrink-0 opacity-50" />
                                <div className="flex flex-col">
                                    <span className="font-medium">{place.structuredFormat?.mainText?.text || place.displayName}</span>
                                    {place.structuredFormat?.secondaryText && (
                                        <span className="text-xs opacity-60">{place.structuredFormat.secondaryText.text}</span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Map Container */}
            <div className={`relative w-full h-80 rounded-3xl overflow-hidden border z-0 ${isLight ? 'border-neutral-200' : 'border-white/10'
                }`}>
                <div className="map-container-wrapper w-full h-full">
                    <LeafletMap
                        center={position}
                        zoom={17}
                        onDragEnd={handleDragEnd}
                    />
                </div>

                {/* Helper Text Overlay */}
                <div className={`absolute bottom-4 left-4 right-4 p-3 rounded-xl backdrop-blur-md text-xs font-medium text-center shadow-lg transition-all ${isLight ? 'bg-white/80 text-neutral-600' : 'bg-black/60 text-neutral-300'
                    }`}>
                    <div className="flex items-center justify-center gap-2">
                        <Navigation size={12} />
                        Drag the pin to the exact entrance location
                    </div>
                </div>
            </div>

            <p className={`text-xs text-center font-medium ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                We'll use this location for weather updates.
            </p>
        </div>
    );
};

const LeafletMap = ({ center, zoom, onDragEnd }) => {
    const markerRef = useRef(null);

    const eventHandlers = useMemo(
        () => ({
            dragend: (e) => {
                onDragEnd(e);
            },
        }),
        [onDragEnd],
    );

    return (
        <MapContainer center={center} zoom={zoom} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker
                draggable={true}
                eventHandlers={eventHandlers}
                position={center}
                ref={markerRef}
            >
            </Marker>
            <MapViewUpdater center={center} />
        </MapContainer>
    );
}

export default LocationPicker;

